import * as React from "react";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import StyledGrid, { FilterChip } from "../../../shared/components/StyledGrid";
import { useGetAssessmentsMutation } from "../../../services/InsuranceService";
import useFetchWithMsal from "../../../utils/useFetchWithMsal";
import { useSelector } from "react-redux";
import { Button, Typography, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { TableFilter } from "../../../components/tableFilter/TableFilter";
import { LoadingPanel } from "../../../shared/components/LoadingPanel";
import { assessmentColumns } from "../../../constants/TablesColumns";
import { getCreateAssessmentPermission } from "../../../shared/utils/permissions";
import { AddItemContainer, AddItemOption, AddItemOptionsContainer } from "./AssessmentTab.styled";
import { ClassesPopup } from "./popups/ClassesPopup";
import { findIdByName } from "../invoicesTab/InvoicesTab";
import { assessmentClasses } from "../invoicesTab/tableFilter/TableFilter";

import { BarChart } from "@mui/icons-material";

export const AssessmentTab = () => {

  const roleId = useSelector((state) => state?.user?.roleId);

  const { skip } = useFetchWithMsal();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [totalCount, setTotalCount] = useState(0);

  const [sortingModel, setSortingModel] = useState();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isShowClassAPopup, setIsShowClassAPopup] = useState(false);
  const [isShowClassBPopup, setIsShowClassBPopup] = useState(false);
  const [isAddItemDropdownVisible, setIsAddItemDropdownVisible] = useState(false);

  const [rowData, setRowData] = useState(null);
  const [getAssessment, { data, isLoading }] = useGetAssessmentsMutation("", { skip });
  const { naicCode, memberName, assessmentClass } = useSelector(
    (state) => state.filter
  );
  // Reports menu state
  const [reportsAnchorEl, setReportsAnchorEl] = useState(null);

  useEffect(() => {
    fetchData()
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortingModel?.field,
    sortingModel?.sort,
  ]);

  useEffect(() => {
    setIsAddItemDropdownVisible(false);
  }, [isShowClassAPopup, isShowClassBPopup]);

  useEffect(() => {
    if (!isLoading) {
      if (data?.totalCount !== totalCount) {
        setTotalCount(data?.totalCount);
      }
    }
  }, [isLoading]);

  const fetchData = (value) => {
    const body = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      orderBy: sortingModel?.field || "",
      orderByDirection: sortingModel?.sort || "",
      naicCode: value ? value.naicCode : naicCode,
      memberName: value ? value.memberName : memberName,
      assessmentClass: value ? findIdByName(assessmentClasses, value.assessmentClass) : findIdByName(assessmentClasses, assessmentClass),
    };
    getAssessment(body);
  };
  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const onApplyFilters = (value) => {
    if (paginationModel.page) {
      setPaginationModel(prev => { return { ...prev, page: 0 } })

    } else {
      fetchData(value)
    }
  };

  const onRowClick = (e) => {
    setRowData(e.row)
    e.row.assessmentTypeKey === 1 ?
      setIsShowClassAPopup(true) :
      setIsShowClassBPopup(true)
  }

  const onPopupClose = () => {
    setRowData(null)
    setIsShowClassAPopup(false);
    setIsShowClassBPopup(false);
    fetchData()
  };
  const onOpenReportsMenu = (e) => setReportsAnchorEl(e.currentTarget);
  const onCloseReportsMenu = () => setReportsAnchorEl(null);
  const openReport = (url, label) => {
    if (!url) {
      /* eslint-disable no-console */
      console.warn(`[AssessmentTab] No URL configured for report: ${label}`);
      /* eslint-enable no-console */
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    onCloseReportsMenu();
  };
  return (
    <Box sx={{ height: "100%", width: "100%", margin: "50px auto 0 auto" }}>
      {isLoading && <LoadingPanel />}
      <Box display={"flex"} sx={{ justifyContent: "space-between" }} mb={3}>
        <Typography variant="h1" color="primary.main" sx={{ marginLeft: "16px" }}>
          Assessments
        </Typography>
        <Box display={"flex"} gap={2} alignItems="center" >
          {getCreateAssessmentPermission(roleId) && (
            <AddItemContainer>
              <Button
                variant="outlined"
                onClick={() => setIsAddItemDropdownVisible(!isAddItemDropdownVisible)}
              >
                Add Assessment
              </Button>
              {isAddItemDropdownVisible && (
                <AddItemOptionsContainer>
                  <AddItemOption onClick={() => setIsShowClassAPopup(true)}>
                    Class A Assessment
                  </AddItemOption>
                  <AddItemOption onClick={() => setIsShowClassBPopup(true)}>
                    Class B Assessment
                  </AddItemOption>
                </AddItemOptionsContainer>
              )}
            </AddItemContainer>
          )}
          <Box display={"flex"} gap={2} alignItems="center" pr={2}>
            <Button
              variant="outlined"
              startIcon={<BarChart fontSize="small" />}
              onClick={onOpenReportsMenu}
            >
              Reports
            </Button>
            <Menu
              anchorEl={reportsAnchorEl}
              open={Boolean(reportsAnchorEl)}
              onClose={onCloseReportsMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem onClick={() => openReport('https://app.powerbi.com/groups/17de80d1-c228-4727-8b60-4f42eb7649dd/reports/cd95a26b-d99d-424f-b7f7-0f896df185db/9dfb3ba70c5407720e03?experience=power-bi', 'Assessment Detail')}>
                <ListItemIcon><BarChart fontSize="small" /></ListItemIcon>
                <ListItemText primary="Assessment Detail" />
              </MenuItem>
              <MenuItem onClick={() => openReport('https://app.powerbi.com/groups/17de80d1-c228-4727-8b60-4f42eb7649dd/reports/cd95a26b-d99d-424f-b7f7-0f896df185db/73b4fa8a1f0227f9fc6f?experience=power-bi', 'Assessments List')}>
                <ListItemIcon><BarChart fontSize="small" /></ListItemIcon>
                <ListItemText primary="Assessments List" />
              </MenuItem>
            </Menu>
            <TableFilter onApplyFilters={onApplyFilters} type="assessment" assessmentClassesFilter />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          marginRight: "15px",
          marginBottom: "15px",
        }}
      >
        {naicCode && <FilterChip>NAIC Code: {naicCode}</FilterChip>}
        {memberName && <FilterChip>Member Name: {memberName}</FilterChip>}
        {assessmentClass && <FilterChip>Assessment Class: {assessmentClass}</FilterChip>}
      </Box>
      <StyledGrid
        data={data?.assesments}
        width="100%"
        columns={assessmentColumns}
        rowCount={totalCount}
        onSortingModelChange={(model) => setSortingModel(model[0])}
        paginationModel={paginationModel}
        setPaginationModel={handlePaginationModelChange}
        onRowClick={onRowClick}
      />
      <ClassesPopup
        classAPopupOpen={isShowClassAPopup}
        classBPopupOpen={isShowClassBPopup}
        open={isShowClassAPopup || isShowClassBPopup}
        onClose={onPopupClose}
        data={rowData}
      />
    </Box>
  );
};
